/**
 * 🎧 FmLanding — "Listen to Cetalingua FM" page for the app (15 Oct 2026).
 *
 * Explains the listener app with real app screenshots (public/marketing/fm-*.jpg) and has the two store buttons.
 * STORE LINKS: paste the links below when the apps are live — an empty link shows the button as "Coming soon"
 * (not clickable). Nothing else needs changing.
 */
import React from 'react';
import './FmLanding.css';
import Showcase from '../common/Showcase';
import fmIcon from '../../assets/fm-icon.png';

export const APP_STORE_URL = '';     // e.g. https://apps.apple.com/app/id0000000000
export const GOOGLE_PLAY_URL = '';   // e.g. https://play.google.com/store/apps/details?id=…

const SHOTS = [
  { file: 'fm-globe.jpg', kind: 'phone', caption: 'Listen: hydrophone stations around the world on a 3D globe' },
  { file: 'fm-station.jpg', kind: 'phone', caption: 'Play a station’s recordings and follow your favourites' },
  { file: 'fm-visualizer.jpg', kind: 'phone', caption: 'Visualizer: live spectrogram and a timeline of the calls that were heard' },
  { file: 'fm-science.jpg', kind: 'phone', caption: 'Citizen science: label real sounds, practise, take quizzes' },
  { file: 'fm-profile.jpg', kind: 'phone', caption: 'Your profile: detections, listening time, streak, levels' },
  { file: 'fm-rewards.jpg', kind: 'phone', caption: 'Badges, leaderboards and points you can turn into coupons' },
];

const FEATURES = [
  { icon: '🌍', title: 'Real ocean sounds, from real stations',
    text: 'Hydrophone stations upload what they record: dolphins, manatees, whales and the sea around them. Pick a station on the globe and listen.' },
  { icon: '📊', title: 'See what you hear',
    text: 'The visualizer draws a live spectrogram while the sound plays, and the timeline marks the calls that our models and acoustic engine found in the recording.' },
  { icon: '🔬', title: 'Help science',
    text: 'Label short clips in Live mode, learn in Practice, test yourself in Quizzes and the daily (Pro) or weekly knowledge quiz. Your labels help train the models that recognise marine mammal calls.' },
  { icon: '🏆', title: 'Points, badges, rewards',
    text: 'Earn points for labelling, quizzes and listening, collect badges, climb the leaderboards — and turn spendable points into coupons.' },
  { icon: '🐋', title: 'Discover',
    text: 'Find whale and dolphin watching companies and other marine businesses in the Discover tab.' },
  { icon: '⭐', title: 'Free, with Pro for more',
    text: 'Listening is free. Pro adds unlimited labelling, the daily quiz and extra privacy choices, and supports the stations and research.' },
];

// Official store badges (public/marketing/badge-*.png, supplied by the user 15 Oct 2026). Until the store link is set
// the badge is shown dimmed with "Coming soon" and is not clickable.
const StoreButton = ({ url, img, alt }) => {
  const badge = <img src={`/marketing/${img}`} alt={alt} className="fm-badge" />;
  return url ? (
    <a className="fm-store-btn" href={url} target="_blank" rel="noopener noreferrer">{badge}</a>
  ) : (
    <span className="fm-store-btn fm-store-soon" aria-disabled="true" title="Coming soon">
      {badge}
      <span className="fm-soon-label">Coming soon</span>
    </span>
  );
};

export const StoreButtons = () => (
  <div className="fm-stores">
    <StoreButton url={APP_STORE_URL} img="badge-app-store.png" alt="Download on the App Store" />
    <StoreButton url={GOOGLE_PLAY_URL} img="badge-google-play.png" alt="Get it on Google Play" />
  </div>
);

const FmLanding = ({ onNavigate, onHostStation }) => (
  <div className="fm-landing">
    <section className="fm-hero">
      <img src={fmIcon} alt="Cetalingua FM app icon" className="fm-hero-icon" />
      <h1>Listen to Cetalingua FM</h1>
      <p className="fm-hero-sub">
        Tune in to dolphins, manatees and whales recorded by hydrophone stations around the world — see their calls as
        they play, and help scientists understand them.
      </p>
      <StoreButtons />
      <p className="fm-hero-note">For iPhone and Android. The apps are on their way to the stores.</p>
    </section>

    <section className="fm-features">
      {FEATURES.map((f) => (
        <div key={f.title} className="fm-feature">
          <div className="fm-feature-icon">{f.icon}</div>
          <h3>{f.title}</h3>
          <p>{f.text}</p>
        </div>
      ))}
    </section>

    <Showcase title="Inside the app" intro="Screens from the Cetalingua FM app." items={SHOTS} />

    <section className="fm-cta">
      <h2>Get the app</h2>
      <StoreButtons />
      <p className="fm-cta-more">
        Record the ocean yourself?{' '}
        <button type="button" className="fm-link" onClick={onHostStation}>Host a station</button>
        {' '}and your recordings play in the app.
      </p>
      <p className="fm-legal">
        <button type="button" className="fm-link" onClick={() => onNavigate && onNavigate('terms')}>Terms of Service</button>
        {' · '}
        <button type="button" className="fm-link" onClick={() => onNavigate && onNavigate('privacy')}>Privacy Policy</button>
      </p>
    </section>
  </div>
);

export default FmLanding;
